import type {
  ShotPlan,
  StoryboardBeatInput,
  StoryboardBeatRole,
  StoryboardBrandInput,
  StoryboardInput,
  StoryboardPipeline,
  StoryboardPromotion,
  StoryboardSubjectType,
  VideoAspectRatio,
} from '@/store/slices/creativeJobs';
import { defaultAdCta, localizedSubject, type LocalizedCopy } from './ad-language';
import type { AdSubject, AdSubjectType } from './banner-kit-api';
import type { CreativeItem } from './creative-api';
import type { Translate } from './video-composer';

/**
 * Pure helpers for the page-local storyboard composer (see `components/StoryboardComposer`). Kept out
 * of the components so the branchy bits (templates, draft mutations, seeding, phase copy, reload
 * survival) are unit-testable without rendering. TYPE-only imports on purpose: a value import would
 * pull the store/axios chains into a jest run (`ad-language` is the one value import: it is pure and imports
 * only static config). `Translate` and `newestJobOfKind` are reused from
 * `video-composer.ts` (which imports only pure data, never the store or a component).
 */
export type { Translate };
export { newestJobOfKind } from './video-composer';

/** Placement recorded on a storyboard job. Like video, the backend keys the "no banner-family
 *  version" invariant on an empty root banner, so this literal is just a label. */
export const STORYBOARD_PLACEMENT = 'storyboard';

/** Beat-count bounds, matching the backend's 2..10 validation (up to 10 beats for long-format videos). */
export const STORYBOARD_MIN_BEATS = 2;
export const STORYBOARD_MAX_BEATS = 10;

/** Max subjects (promotions, games or features) a video ad advertises. Every subject is a chapter that builds up to
 *  its own key-art peak in at least one 4s beat, plus the closing CTA beat (the backend's beat plan), so four keeps a
 *  30s ad readable; shorter lengths allow fewer (see maxSubjectsForLength). */
export const STORYBOARD_MAX_PROMOTIONS = 4;

/** How many subjects fit a target length (seconds): 10s 1, 15s 2, 20s 3, 30s (and Auto) 4 — one build-up beat per
 *  subject plus the closing beat within the length's beat budget. */
export const maxSubjectsForLength = (targetSeconds?: number): number => {
  if (!targetSeconds) return STORYBOARD_MAX_PROMOTIONS;
  if (targetSeconds <= 10) return 1;
  if (targetSeconds <= 15) return 2;
  if (targetSeconds <= 20) return 3;
  return STORYBOARD_MAX_PROMOTIONS;
};

/** The shortest target length (seconds) that fits n subjects: maxSubjectsForLength's inverse (undefined for one,
 *  which the default length already fits). Pure. */
export const lengthForSubjects = (n: number): number | undefined => {
  if (n <= 1) return undefined;
  if (n === 2) return 15;
  if (n === 3) return 20;
  return 30;
};

/** Default per-beat duration in seconds. 8 is valid for Veo (which accepts only 4/6/8). */
export const BEAT_DURATION_DEFAULT = 8;

/** The dramatic roles a beat can take, in a typical ad arc. */
export const BEAT_ROLES: StoryboardBeatRole[] = ['hook', 'offer', 'proof', 'cta'];

/**
 * The single source of truth the canvas is a view over: the target aspect, the end-card CTA, and the
 * ordered beats. The node graph is DERIVED from this on every change.
 */
export interface StoryboardDraft {
  aspect: VideoAspectRatio;
  cta: string;
  /** The promotions this storyboard advertises (multi-promo). promotions[0] is the key visual (F0). Each
   *  beat's `promotion_index` points into this array. A single-promo draft carries exactly one. */
  promotions: StoryboardPromotion[];
  beats: StoryboardBeatInput[];
  /** Owner-supplied mood-board reference images (CDN urls) that steer the WHOLE video's look and feel,
   *  enhancing (not replacing) the promotion key art. Sent on compose + generate; persisted with the draft. */
  moodImages: string[];
  /** The owner's chosen total ad length (10/15/20/30). Drives the beat count: on Generate storyboard the
   *  refine LLM is told to make the per-beat durations sum near this. Persisted; 0/undefined = let the
   *  template decide. Single clip: the exact clip length L (always one of the model's clip lengths). */
  targetDurationSeconds?: number;
  /** Which pipeline renders this ad: `single_clip` (v2, one clip from a shot plan) or `beats` (v1). Fixed per ad
   *  (set by the video tab that created it); every path branches on this. */
  pipeline: StoryboardPipeline;
  /** Single clip only: the composed, owner-editable shot plan. */
  shotPlan?: ShotPlan;
  /** v1 only: the ad-level instrumental music brief the compose wrote (hidden, never edited in the UI). Every
   *  beat's clip scores the same piece from it. Replaced (or cleared) by every recompose; persisted with the
   *  storyboard snapshot and sent on generate / resume. */
  music?: string;
  /** The ad's language: a brand website language id (`mx`, `br`, `en`...). The promotions' copy is synced into it
   *  ({@link localizePromotionsCopy}) and the compose writes the call to action in it. '' / undefined = an older ad,
   *  rendered as before (its record copy as the creatives list shows it). Persisted and sent on compose + generate. */
  language?: string;
  /** The language the on-screen storyboard was composed in, when the owner has since picked another one without
   *  composing again (its call to action is written in it): saved with the draft so a reopened ad still asks for a
   *  new compose. Absent = as `language`. */
  composedLanguage?: string;
}

/** Map a promotion (banner) item to a storyboard promotion (its key visual + on-site offer copy). */
export const promotionFromItem = (item: CreativeItem): StoryboardPromotion => ({
  banner_id: item.banner_id,
  image_url: item.image_url ?? '',
  headline: item.headline ?? '',
  subtitle: item.subtitle ?? '',
  cta_text: item.cta_text ?? '',
});

/** A stable identity of a promotion SET (banner ids in order), used to key the compose cache + detect a
 *  changed set. */
export const promotionKey = (promotions: StoryboardPromotion[]): string =>
  promotions.map((p) => p.banner_id || p.image_url).join('+');

/** The draft's key visual (F0) = the first promotion's image, or undefined when there are none. */
export const keyArtOf = (draft: StoryboardDraft): string | undefined => draft.promotions[0]?.image_url || undefined;

/** A CONTIGUOUS distribution of `beatCount` beats across `nPromos` promotions: one run per promotion, sized
 *  as evenly as possible (extra beats go to the earlier runs). E.g. (3,2)->[0,0,1], (4,3)->[0,0,1,2],
 *  (2,3)->[0,1] (fewer beats than promos: the trailing promos get none). */
export const distributeBeats = (beatCount: number, nPromos: number): number[] => {
  if (nPromos <= 1) return new Array(Math.max(0, beatCount)).fill(0);
  const runs = Math.min(beatCount, nPromos);
  const base = Math.floor(beatCount / runs);
  const extra = beatCount % runs;
  const out: number[] = [];
  for (let p = 0; p < runs; p += 1) {
    const len = base + (p < extra ? 1 : 0);
    for (let k = 0; k < len; k += 1) out.push(p);
  }
  return out;
};

/** Ensure every beat has a valid promotion_index: clamp out-of-range to 0, and if there are multiple
 *  promotions but every beat sits on 0 (an un-distributed set), apply the default contiguous distribution. */
export const ensureDistribution = (beats: StoryboardBeatInput[], nPromos: number): StoryboardBeatInput[] => {
  const clamped = beats.map((b) => {
    const idx = b.promotion_index ?? 0;
    return idx < 0 || idx >= nPromos ? { ...b, promotion_index: 0 } : b;
  });
  if (nPromos > 1 && clamped.every((b) => (b.promotion_index ?? 0) === 0)) {
    const dist = distributeBeats(clamped.length, nPromos);
    return clamped.map((b, i) => ({ ...b, promotion_index: dist[i] ?? 0 }));
  }
  return clamped;
};

/** Set the draft's promotions (capped at the max), REMAPPING each beat's promotion to survive add/remove/
 *  reorder of the set: a beat keeps its promotion by banner id where possible, else falls back to 0. */
export const setPromotions = (draft: StoryboardDraft, next: StoryboardPromotion[]): StoryboardDraft => {
  const promotions = next.slice(0, STORYBOARD_MAX_PROMOTIONS);
  const oldIds = draft.promotions.map((p) => p.banner_id || p.image_url);
  const newIndexById = new Map(promotions.map((p, i) => [p.banner_id || p.image_url, i]));
  const beats = draft.beats.map((b) => {
    const oldId = oldIds[b.promotion_index ?? 0];
    const remapped = oldId !== undefined ? newIndexById.get(oldId) : undefined;
    return { ...b, promotion_index: remapped ?? 0 };
  });
  return { ...draft, promotions, beats };
};

/** A storyboard promotion as the ad-language helpers read a subject (only its id and copy matter there). */
const promotionAsSubject = (p: StoryboardPromotion): AdSubject => ({
  id: p.banner_id,
  name: p.headline ?? '',
  image_url: p.image_url,
  headline: p.headline ?? '',
  subtitle: p.subtitle ?? '',
  cta_text: p.cta_text ?? '',
  cta_href: '',
});

/**
 * The draft with its promotions' on-screen copy in the ad's language. `copies` holds each promotion's copy in that
 * language by banner id (the website's own text, else Lumio's translation; see ad-language-api). The headline and
 * subtitle always follow it. The call to action follows it only while no storyboard is composed: the compose then
 * writes the ad's own call to action in the language, which this must never undo; the draft's CTA moves with the
 * first promotion's when it was that one (or empty). An empty localized call to action becomes the promotion default
 * in the language (localizedSubject). Never changes a banner id or an image. The same draft when there is nothing to
 * change (no language, no copy for it, or already in sync), so it is safe to apply on every render. Pure.
 */
export const localizePromotionsCopy = (
  draft: StoryboardDraft,
  copies: Readonly<Record<string, LocalizedCopy>>,
): StoryboardDraft => {
  const language = (draft.language ?? '').trim();
  if (!language) return draft;
  // Pipeline-aware, as the composer reads it: a single clip may still carry leftover v1 beats.
  const composed = draft.pipeline === 'single_clip' ? Boolean(draft.shotPlan) : draft.beats.length > 0;
  let changed = false;
  const promotions = draft.promotions.map((p) => {
    const loc = p.banner_id ? copies[p.banner_id] : undefined;
    if (!loc || loc.language !== language || loc.subject_id !== p.banner_id) return p;
    const s = localizedSubject(promotionAsSubject(p), loc, language, 'promotion');
    const ctaText = composed ? (p.cta_text ?? '') : (s.cta_text ?? '');
    if (s.headline === (p.headline ?? '') && s.subtitle === (p.subtitle ?? '') && ctaText === (p.cta_text ?? '')) {
      return p;
    }
    changed = true;
    return { ...p, headline: s.headline, subtitle: s.subtitle, cta_text: ctaText };
  });
  if (!changed) return draft;
  const firstCta = draft.promotions[0]?.cta_text ?? '';
  const followsFirst = !composed && (draft.cta.trim() === '' || draft.cta === firstCta);
  const cta = followsFirst ? (promotions[0]?.cta_text ?? draft.cta) : draft.cta;
  return { ...draft, promotions, cta };
};

/**
 * The promotions with their calls to action in `language`, for a compose in it after an earlier compose in another
 * one: a promotion's own CTA in the language (`copies`), else the promotion default in it; a game or loyalty ad's
 * `subjectDefault`. The compose then never keeps the previous language's CTA, even when it falls back to the base
 * beats (no CTA of its own). Pure.
 */
export const promotionsCtaIn = (
  promotions: readonly StoryboardPromotion[],
  copies: Readonly<Record<string, LocalizedCopy>>,
  language: string,
  subjectType: AdSubjectType,
  subjectDefault: string,
): StoryboardPromotion[] =>
  promotions.map((p) => {
    if (subjectType !== 'promotion') {
      return { ...p, cta_text: subjectDefault || defaultAdCta(subjectType, language) };
    }
    const loc = p.banner_id ? copies[p.banner_id] : undefined;
    const s = localizedSubject(promotionAsSubject({ ...p, cta_text: '' }), loc, language, 'promotion');
    return { ...p, cta_text: s.cta_text ?? '' };
  });

/** Interpolation values a template beat's copy is seeded from (a promotion's on-site text). */
export type TemplateVars = Record<string, string>;

/** One beat of a curated template. `motion`/`frame` are functions of `t` so the English copy sits
 *  inside a `t()` call and is harvested by `npm run i18n:extract` (a plain string in a data array is
 *  invisible to the extractor). */
export interface StoryboardTemplateBeat {
  role: StoryboardBeatRole;
  duration: number;
  motion: (t: Translate, vars: TemplateVars) => string;
  frame: (t: Translate, vars: TemplateVars) => string;
}

export type StoryboardTemplateId =
  | 'big-win-hype'
  | 'new-game-teaser'
  | 'viral-moment'
  | 'jackpot-drop'
  | 'hot-streak'
  | 'cinematic-reveal'
  | 'countdown-rush'
  | 'vip-luxury'
  | 'neon-nights'
  | 'coin-storm';

/** A curated starting point: a title and an ordered set of beats. `title` is a function of `t` for the
 *  same extraction reason as the beat copy. */
export interface StoryboardTemplate {
  id: StoryboardTemplateId;
  title: (t: Translate) => string;
  beats: StoryboardTemplateBeat[];
}

/**
 * Ten curated vibe presets (NOT offer presets; the promotion supplies the offer copy, a template supplies
 * the STYLE and arc). Beat counts vary 2..4 and every duration is 6 or 8 seconds: model-valid for both Veo
 * and Seedance so nothing snaps, and 10s is not used because no model accepts it. Motion stays purposeful
 * and physically plausible (push-ins,
 * sweeps, reveals) since interior frames are edited from the key visual and the models animate coherent
 * camera/light moves most reliably.
 */
export const STORYBOARD_TEMPLATES: StoryboardTemplate[] = [
  {
    id: 'big-win-hype',
    title: (t) => t('Big win hype'),
    beats: [
      {
        role: 'hook',
        duration: 6,
        motion: (t, v) =>
          t(
            'Explosive punch-in on the artwork as gold coins burst outward and the "{{headline}}" offer slams in as huge, high-energy on-brand headline text.',
            v,
          ),
        frame: (t, v) =>
          t(
            'The {{brand}} scene mid-explosion, gold coins scattering, the "{{headline}}" offer blazing across the frame as bold on-brand headline text.',
            v,
          ),
      },
      {
        role: 'proof',
        duration: 8,
        motion: (t, v) =>
          t(
            'Whip around the celebration as the win detonates, light flares sweeping while the "{{subtitle}}" detail rockets in as bright supporting text.',
            v,
          ),
        frame: (t, v) =>
          t(
            'A euphoric win moment for {{brand}}, coins and confetti raining, the "{{subtitle}}" detail shown as crisp, glowing on-brand text.',
            v,
          ),
      },
      {
        role: 'cta',
        duration: 6,
        motion: (t, v) =>
          t('Snap-zoom to the payoff as the "{{cta}}" call to action ignites as a bold, tappable on-screen button.', v),
        frame: (t, v) =>
          t(
            'A triumphant closing frame with the "{{cta}}" call to action rendered prominently as a glowing button over the {{brand}} celebration.',
            v,
          ),
      },
    ],
  },
  {
    id: 'new-game-teaser',
    title: (t) => t('New game teaser'),
    beats: [
      {
        role: 'hook',
        duration: 8,
        motion: (t, v) =>
          t(
            'Slow reveal from deep shadow as the {{brand}} hero emerges and the "{{headline}}" offer fades up as bold, on-brand headline text.',
            v,
          ),
        frame: (t, v) =>
          t(
            'The scene fully lit and cinematic, the "{{headline}}" offer set as bold on-brand headline text over the new-game hero.',
            v,
          ),
      },
      {
        role: 'offer',
        duration: 8,
        motion: (t, v) =>
          t(
            'Drift across the reveal to present the game while the "{{subtitle}}" detail settles in as clean supporting text.',
            v,
          ),
        frame: (t, v) =>
          t(
            'A polished hero shot of the new {{brand}} game with the "{{subtitle}}" detail shown as crisp, legible on-brand text.',
            v,
          ),
      },
      {
        role: 'cta',
        duration: 6,
        motion: (t, v) =>
          t(
            'Ease onto the full teaser as the "{{cta}}" call to action fades smoothly in as a bold on-screen button.',
            v,
          ),
        frame: (t, v) =>
          t(
            'A sleek closing frame of the {{brand}} game with the "{{cta}}" call to action rendered prominently as a bold button.',
            v,
          ),
      },
    ],
  },
  {
    id: 'viral-moment',
    title: (t) => t('Viral moment'),
    beats: [
      {
        role: 'hook',
        duration: 6,
        motion: (t, v) =>
          t(
            'Fast handheld whip-pan onto the artwork with a punchy zoom as the "{{headline}}" offer pops in as bold, scroll-stopping on-brand text.',
            v,
          ),
        frame: (t, v) =>
          t(
            'A bold, trending-style frame of {{brand}}, high-contrast and punchy, the "{{headline}}" offer set as loud on-brand headline text.',
            v,
          ),
      },
      {
        role: 'offer',
        duration: 6,
        motion: (t, v) =>
          t('Quick snap-cut push toward the offer as the "{{subtitle}}" detail flicks in as clean supporting text.', v),
        frame: (t, v) =>
          t(
            'A vibrant social-first shot of the "{{headline}}" offer, the "{{subtitle}}" detail shown as crisp, legible on-brand text.',
            v,
          ),
      },
      {
        role: 'proof',
        duration: 6,
        motion: (t, v) =>
          t('Rapid punch-in on the reaction moment as coins and sparkles burst for a shareable beat.', v),
        frame: (t, v) =>
          t(
            'An energetic, meme-ready frame of the {{brand}} win with coins and sparkles exploding across the composition.',
            v,
          ),
      },
      {
        role: 'cta',
        duration: 6,
        motion: (t, v) =>
          t('Hard cut to the payoff as the "{{cta}}" call to action snaps in as a bold, tappable on-screen button.', v),
        frame: (t, v) =>
          t(
            'A punchy closing frame with the "{{cta}}" call to action rendered prominently as a bold button over the {{brand}} scene.',
            v,
          ),
      },
    ],
  },
  {
    id: 'jackpot-drop',
    title: (t) => t('Jackpot drop'),
    beats: [
      {
        role: 'hook',
        duration: 8,
        motion: (t, v) =>
          t(
            'Rising crane on the artwork as the jackpot detonates, coins erupting while the "{{headline}}" offer explodes in as massive, on-brand headline text.',
            v,
          ),
        frame: (t, v) =>
          t(
            'The {{brand}} jackpot moment at full blast, a fountain of gold coins framing the "{{headline}}" offer as bold on-brand headline text.',
            v,
          ),
      },
      {
        role: 'cta',
        duration: 8,
        motion: (t, v) =>
          t(
            'Sweep down through the coin storm to the payoff as the "{{cta}}" call to action lights up as a bold on-screen button, the "{{subtitle}}" detail crisp beside it.',
            v,
          ),
        frame: (t, v) =>
          t(
            'A euphoric closing frame of the {{brand}} jackpot with the "{{cta}}" call to action as a glowing button and the "{{subtitle}}" detail shown as clean on-brand text.',
            v,
          ),
      },
    ],
  },
  {
    id: 'hot-streak',
    title: (t) => t('Hot streak'),
    beats: [
      {
        role: 'hook',
        duration: 6,
        motion: (t, v) =>
          t(
            'Fast push-in on the artwork as a win lands and the "{{headline}}" offer snaps in as bold, on-brand headline text.',
            v,
          ),
        frame: (t, v) =>
          t(
            'The {{brand}} scene on a high, the "{{headline}}" offer set as bold on-brand headline text with a first win glowing.',
            v,
          ),
      },
      {
        role: 'proof',
        duration: 6,
        motion: (t, v) => t('Quick dolly as a second win hits, multiplier lights stacking and the energy climbing.', v),
        frame: (t, v) =>
          t('An escalating win frame for {{brand}}, stacked multipliers and glowing coins building momentum.', v),
      },
      {
        role: 'proof',
        duration: 8,
        motion: (t, v) =>
          t(
            'Accelerate through the streak as wins cascade and the "{{subtitle}}" detail rises in as bright supporting text.',
            v,
          ),
        frame: (t, v) =>
          t(
            'A peak-momentum shot of the {{brand}} streak, cascading wins and coins framing the "{{subtitle}}" detail as crisp on-brand text.',
            v,
          ),
      },
      {
        role: 'cta',
        duration: 6,
        motion: (t, v) =>
          t('Ride the momentum to the payoff as the "{{cta}}" call to action ignites as a bold on-screen button.', v),
        frame: (t, v) =>
          t(
            'A high-energy closing frame with the "{{cta}}" call to action rendered prominently as a bold button over the {{brand}} streak.',
            v,
          ),
      },
    ],
  },
  {
    id: 'cinematic-reveal',
    title: (t) => t('Cinematic reveal'),
    beats: [
      {
        role: 'hook',
        duration: 8,
        motion: (t, v) =>
          t(
            'Slow, luxurious dolly through soft light and depth as the {{brand}} hero glides into view and the "{{headline}}" offer fades in as elegant, on-brand headline text.',
            v,
          ),
        frame: (t, v) =>
          t(
            'A premium, cinematic frame of {{brand}} with rich lighting and depth, the "{{headline}}" offer set as refined on-brand headline text.',
            v,
          ),
      },
      {
        role: 'offer',
        duration: 8,
        motion: (t, v) =>
          t(
            'Graceful glide across the scene as the offer is unveiled and the "{{subtitle}}" detail settles in as clean supporting text.',
            v,
          ),
        frame: (t, v) =>
          t(
            'An elegant hero shot of the "{{headline}}" offer, the "{{subtitle}}" detail shown as crisp, refined on-brand text.',
            v,
          ),
      },
      {
        role: 'cta',
        duration: 8,
        motion: (t, v) =>
          t(
            'Ease into a confident final composition as the "{{cta}}" call to action resolves smoothly as a premium on-screen button.',
            v,
          ),
        frame: (t, v) =>
          t(
            'A high-end closing frame of the {{brand}} promotion with the "{{cta}}" call to action rendered as a clean, premium button.',
            v,
          ),
      },
    ],
  },
  {
    id: 'countdown-rush',
    title: (t) => t('Countdown rush'),
    beats: [
      {
        role: 'hook',
        duration: 6,
        motion: (t, v) =>
          t(
            'Snap push-in on the artwork with ticking-clock energy as the "{{headline}}" offer flashes in as urgent, bold on-brand headline text.',
            v,
          ),
        frame: (t, v) =>
          t(
            'A high-urgency frame of {{brand}}, the "{{headline}}" offer set as bold on-brand headline text with a countdown pulse.',
            v,
          ),
      },
      {
        role: 'proof',
        duration: 6,
        motion: (t, v) =>
          t(
            'Fast cut as the stakes rise, numbers and lights racing while the "{{subtitle}}" detail snaps in as bright supporting text.',
            v,
          ),
        frame: (t, v) =>
          t(
            'An urgent shot of the {{brand}} offer, racing lights and the "{{subtitle}}" detail shown as crisp on-brand text.',
            v,
          ),
      },
      {
        role: 'cta',
        duration: 6,
        motion: (t, v) =>
          t(
            'Hard drive to the payoff as the "{{cta}}" call to action slams in as a bold, act-now on-screen button.',
            v,
          ),
        frame: (t, v) =>
          t(
            'A do-not-miss closing frame with the "{{cta}}" call to action rendered prominently as a bold button over the {{brand}} scene.',
            v,
          ),
      },
    ],
  },
  {
    id: 'vip-luxury',
    title: (t) => t('VIP luxury'),
    beats: [
      {
        role: 'hook',
        duration: 8,
        motion: (t, v) =>
          t(
            'Slow, opulent push through gold light and rich texture as the {{brand}} hero commands the frame and the "{{headline}}" offer glides in as premium, on-brand headline text.',
            v,
          ),
        frame: (t, v) =>
          t(
            'A lavish, high-roller frame of {{brand}} in gold and black, the "{{headline}}" offer set as elegant on-brand headline text.',
            v,
          ),
      },
      {
        role: 'offer',
        duration: 8,
        motion: (t, v) =>
          t(
            'Luxurious glide around the offer as the "{{subtitle}}" detail settles in as clean, refined supporting text.',
            v,
          ),
        frame: (t, v) =>
          t(
            'An opulent hero shot of the "{{headline}}" offer, the "{{subtitle}}" detail shown as crisp, premium on-brand text.',
            v,
          ),
      },
      {
        role: 'cta',
        duration: 8,
        motion: (t, v) =>
          t(
            'Settle into a confident, exclusive final frame as the "{{cta}}" call to action resolves as a polished, premium on-screen button.',
            v,
          ),
        frame: (t, v) =>
          t(
            'A prestige closing frame of the {{brand}} promotion with the "{{cta}}" call to action rendered as a refined gold button.',
            v,
          ),
      },
    ],
  },
  {
    id: 'neon-nights',
    title: (t) => t('Neon nights'),
    beats: [
      {
        role: 'hook',
        duration: 6,
        motion: (t, v) =>
          t(
            'Vibrant push-in through neon glow and reflections as the "{{headline}}" offer flickers in as bold, electric on-brand headline text.',
            v,
          ),
        frame: (t, v) =>
          t(
            'A neon-drenched night frame of {{brand}}, glowing signage and reflections, the "{{headline}}" offer set as electric on-brand headline text.',
            v,
          ),
      },
      {
        role: 'offer',
        duration: 8,
        motion: (t, v) =>
          t(
            'Glide through the neon scene as the offer lights up and the "{{subtitle}}" detail pulses in as clean supporting text.',
            v,
          ),
        frame: (t, v) =>
          t(
            'A vivid neon shot of the "{{headline}}" offer, the "{{subtitle}}" detail shown as crisp, glowing on-brand text.',
            v,
          ),
      },
      {
        role: 'proof',
        duration: 6,
        motion: (t, v) => t('Punch into the party energy as coins and light streaks burst across the neon scene.', v),
        frame: (t, v) =>
          t(
            'A high-energy neon frame of the {{brand}} win, coins and light streaks exploding across the composition.',
            v,
          ),
      },
      {
        role: 'cta',
        duration: 8,
        motion: (t, v) =>
          t('Sweep to the payoff as the "{{cta}}" call to action ignites as a bold, glowing on-screen button.', v),
        frame: (t, v) =>
          t(
            'An electric closing frame with the "{{cta}}" call to action rendered prominently as a glowing neon button over the {{brand}} scene.',
            v,
          ),
      },
    ],
  },
  {
    id: 'coin-storm',
    title: (t) => t('Coin storm'),
    beats: [
      {
        role: 'hook',
        duration: 8,
        motion: (t, v) =>
          t(
            'Push up into a rising storm of gold coins as the "{{headline}}" offer erupts in as huge, on-brand headline text.',
            v,
          ),
        frame: (t, v) =>
          t(
            'The {{brand}} scene inside a storm of gold coins, the "{{headline}}" offer blazing across the frame as bold on-brand headline text.',
            v,
          ),
      },
      {
        role: 'proof',
        duration: 6,
        motion: (t, v) =>
          t(
            'Whirl through the cascading coins as the reward peaks and the "{{subtitle}}" detail rockets in as bright supporting text.',
            v,
          ),
        frame: (t, v) =>
          t(
            'A wealth-explosion frame for {{brand}}, cascading coins framing the "{{subtitle}}" detail as crisp, glowing on-brand text.',
            v,
          ),
      },
      {
        role: 'cta',
        duration: 8,
        motion: (t, v) =>
          t(
            'Sweep down through the coins to the payoff as the "{{cta}}" call to action lights up as a bold on-screen button.',
            v,
          ),
        frame: (t, v) =>
          t(
            'A triumphant closing frame of the {{brand}} coin storm with the "{{cta}}" call to action rendered prominently as a glowing button.',
            v,
          ),
      },
    ],
  },
];

/** Look up a template by id (falls back to the first preset). */
export const templateById = (id: string): StoryboardTemplate =>
  STORYBOARD_TEMPLATES.find((tpl) => tpl.id === id) ?? STORYBOARD_TEMPLATES[0];

/**
 * Seed a fresh draft from a template + a promotion's on-site copy. Every end frame starts as a
 * `generate` frame described by the template; the CTA defaults to the promotion's button text (or the
 * brand name). `t` is injected so it is unit-testable with an identity translator, and so the seeded
 * copy is already in the owner's language.
 */
export const seedDraft = (
  template: StoryboardTemplate,
  promotions: StoryboardPromotion[],
  brandName: string,
  aspect: VideoAspectRatio,
  t: Translate,
  targetDurationSeconds?: number,
): StoryboardDraft => {
  const promos =
    promotions.length > 0 ? promotions : [{ banner_id: '', image_url: '', headline: '', subtitle: '', cta_text: '' }];
  // Auto-distribute the template's beats across the promotions in contiguous runs; each beat's copy is seeded
  // from ITS promotion, and its promotion_index recorded (the LLM re-decides the split on compose).
  const dist = distributeBeats(template.beats.length, promos.length);
  const varsFor = (p: StoryboardPromotion): TemplateVars => ({
    headline: p.headline ?? '',
    subtitle: p.subtitle ?? '',
    cta: p.cta_text ?? '',
    brand: brandName,
  });
  const beats: StoryboardBeatInput[] = template.beats.map((b, i) => {
    const p = promos[dist[i] ?? 0] ?? promos[0];
    const vars = varsFor(p);
    return {
      role: b.role,
      duration_seconds: b.duration,
      motion_intent: b.motion(t, vars),
      end_frame: { source: 'generate', prompt: b.frame(t, vars) },
      promotion_index: dist[i] ?? 0,
    };
  });
  return {
    aspect,
    cta: promos[0].cta_text || brandName,
    promotions: promos,
    beats,
    moodImages: [],
    targetDurationSeconds,
    pipeline: 'beats',
  };
};

/** Max length (code points) of the vibe brief sent to the single-clip compose (spec 4.2). */
export const VIBE_BRIEF_MAX = 800;

/**
 * The English vibe brief for a single-clip compose: the template's TITLE only (e.g. "Big win hype"), as tone
 * guidance for the compose LLM. Its beats' motion lines are v1 copy (quoted offer text, on-screen buttons, a
 * "Deposit" ask) that contradicts the single clip's no-text rules, so they are left out. `t` must be an ENGLISH
 * translator (the composer passes `i18n.getFixedT('en')`), so the brief is English whatever the owner's language.
 * Capped at {@link VIBE_BRIEF_MAX} code points.
 */
export const buildVibeBrief = (template: StoryboardTemplate, t: Translate): string => {
  const brief = template.title(t);
  const chars = Array.from(brief);
  return chars.length > VIBE_BRIEF_MAX ? chars.slice(0, VIBE_BRIEF_MAX).join('') : brief;
};

/** Immutable: patch one beat by index (no-op for an out-of-range index). */
export const updateBeat = (draft: StoryboardDraft, i: number, patch: Partial<StoryboardBeatInput>): StoryboardDraft => {
  if (i < 0 || i >= draft.beats.length) return draft;
  return { ...draft, beats: draft.beats.map((b, idx) => (idx === i ? { ...b, ...patch } : b)) };
};

/** Immutable: append a `cta` beat with sensible defaults (no-op at the max beat count). */
export const addBeat = (draft: StoryboardDraft, t: Translate): StoryboardDraft => {
  if (draft.beats.length >= STORYBOARD_MAX_BEATS) return draft;
  const beat: StoryboardBeatInput = {
    role: 'cta',
    duration_seconds: BEAT_DURATION_DEFAULT,
    motion_intent: t('Hold steadily on the final scene and invite the viewer to act.'),
    end_frame: {
      source: 'generate',
      prompt: t('A confident closing frame with the offer and call to action clearly in view.'),
    },
    // Inherit the last beat's promotion so a new beat joins the current promotion's run (multi-promo).
    promotion_index: draft.beats[draft.beats.length - 1]?.promotion_index ?? 0,
  };
  return { ...draft, beats: [...draft.beats, beat] };
};

/** Immutable: remove one beat by index (no-op at the min beat count or an out-of-range index). */
export const removeBeat = (draft: StoryboardDraft, i: number): StoryboardDraft => {
  if (draft.beats.length <= STORYBOARD_MIN_BEATS) return draft;
  if (i < 0 || i >= draft.beats.length) return draft;
  return { ...draft, beats: draft.beats.filter((_b, idx) => idx !== i) };
};

/** Snap every beat's duration to the video model's supported set (Veo accepts only 4/6/8; other values
 *  500 upstream). `durations` is the model's list; returns the same object when nothing changes. */
export const snapDraftDurations = (draft: StoryboardDraft, durations: number[]): StoryboardDraft => {
  if (durations.length === 0) return draft;
  const snap = (s: number): number =>
    durations.reduce((best, v) => (Math.abs(v - s) < Math.abs(best - s) ? v : best), durations[0]);
  let changed = false;
  const beats = draft.beats.map((b) => {
    const d = snap(b.duration_seconds);
    if (d === b.duration_seconds) return b;
    changed = true;
    return { ...b, duration_seconds: d };
  });
  return changed ? { ...draft, beats } : draft;
};

/** Immutable: move a beat one slot left (-1) or right (+1); no-op at the ends. */
export const moveBeat = (draft: StoryboardDraft, i: number, dir: -1 | 1): StoryboardDraft => {
  const j = i + dir;
  if (i < 0 || i >= draft.beats.length || j < 0 || j >= draft.beats.length) return draft;
  const beats = [...draft.beats];
  const [moved] = beats.splice(i, 1);
  beats.splice(j, 0, moved);
  return { ...draft, beats };
};

/** Whether a draft can be generated: 2..10 beats, every promotion has a key visual AND owns at least one
 *  beat (else the owner pays for a promotion that never appears), every motion non-empty, generate frames
 *  have a prompt, and url frames have a url. */
export const draftIsReady = (draft: StoryboardDraft): boolean => {
  // Single clip: exactly ONE subject with a key visual, and a plan that covers the clip length exactly.
  if (draft.pipeline === 'single_clip') {
    return (
      draft.promotions.length === 1 &&
      draft.promotions[0].image_url.trim() !== '' &&
      shotPlanLooksValid(draft.shotPlan, draft.targetDurationSeconds)
    );
  }
  if (draft.promotions.length < 1 || !draft.promotions.every((p) => p.image_url.trim() !== '')) return false;
  if (draft.beats.length < STORYBOARD_MIN_BEATS || draft.beats.length > STORYBOARD_MAX_BEATS) return false;
  // Multi-promo: every promotion must be advertised by at least one beat (guards a paid run where a picked
  // promotion, un-distributed, would silently never render). Re-open Regenerate to redistribute.
  if (draft.promotions.length > 1) {
    const used = new Set(draft.beats.map((b) => b.promotion_index ?? 0));
    for (let p = 0; p < draft.promotions.length; p += 1) if (!used.has(p)) return false;
  }
  return draft.beats.every((b) => {
    if (b.motion_intent.trim() === '') return false;
    if (b.end_frame.source === 'generate') return b.end_frame.prompt.trim() !== '';
    return b.end_frame.url.trim() !== '';
  });
};

/** Extra, pipeline-independent fields of the generate `storyboard` payload. */
export interface StoryboardInputExtras {
  subjectType?: StoryboardSubjectType;
  /** The owner's own free-text prompt (may be empty). */
  prompt?: string;
  /** Single clip only: the English vibe brief. */
  vibe?: string;
}

/** Assemble the backend `storyboard` payload from a draft, its template id, the brand identity and
 *  the chosen video model (omitted from the body when unset → backend default). A single-clip draft sends the
 *  spec 4.5 shape: `pipeline`, `shot_plan`, exactly one promotion, `beats: []` and the clip length L. A v1 draft
 *  keeps today's payload (no `pipeline` key = beats) plus the subject type, owner prompt and, when the compose
 *  wrote one, the shared music brief (a single clip never sends it). */
export const toStoryboardInput = (
  draft: StoryboardDraft,
  templateId: string,
  brand: StoryboardBrandInput,
  videoModel?: string,
  extras: StoryboardInputExtras = {},
): StoryboardInput => {
  const common = {
    template_id: templateId,
    ...(videoModel ? { video_model: videoModel } : {}),
    brand,
    ...(draft.targetDurationSeconds ? { target_duration_seconds: draft.targetDurationSeconds } : {}),
    ...(extras.subjectType ? { subject_type: extras.subjectType } : {}),
    ...(extras.prompt ? { prompt: extras.prompt } : {}),
    // Rides the render so the rendered version keeps it (the worker stores the job's params on the version).
    ...(draft.language ? { language: draft.language } : {}),
  };
  if (draft.pipeline === 'single_clip') {
    return {
      ...common,
      promotions: draft.promotions.slice(0, 1),
      beats: [],
      pipeline: 'single_clip',
      ...(draft.shotPlan ? { shot_plan: draft.shotPlan } : {}),
      ...(extras.vibe ? { vibe: extras.vibe } : {}),
    };
  }
  return {
    ...common,
    promotions: draft.promotions,
    beats: draft.beats,
    ...(draft.music?.trim() ? { music: draft.music.trim() } : {}),
  };
};

/** The credit cost of a storyboard = per-beat price x beat count, or undefined until priced. */
export const storyboardCost = (perBeat: number | undefined, beats: number): number | undefined =>
  perBeat === undefined ? undefined : perBeat * beats;

/* ---------- single clip (v2) ---------- */

/** The paired quotation forms the backend strips (with their content) from a shot's action (clipprompt
 *  `quotedSpans`, same order: „…" closes with a quote the later patterns also open with). */
const QUOTED_SPANS: RegExp[] = [/„[^"“”„]*["“”]/g, /“[^”]*”/g, /‘[^’]*’/g, /"[^"]*"/g, /«[^»]*»/g, /¡[^!]*!/g];
/** A '…' used AS A QUOTE (clipprompt `singleQuoted`): an apostrophe inside a word is never matched. */
const SINGLE_QUOTED = /(^|[\s([{])'[^'\s][^']*'($|[\s.,;:!?)\]}])/g;
/** Unpaired quotation marks left once the paired spans are gone (clipprompt `strayQuotes`). */
const STRAY_QUOTES = /["“”„«»¡]/g;

/** What is left of an action once the backend's quote stripping has run (only that; the speech-verb and vocal-cue
 *  lexicons are not mirrored), so a quote-only action reads as empty here exactly as the backend rejects it. */
const withoutQuotes = (s: string): string => {
  let out = QUOTED_SPANS.reduce((acc, re) => acc.replace(re, ' '), s);
  for (let i = 0; i < 8; i += 1) {
    // Each pass may consume the boundary a neighbouring quote needs, so loop until nothing matches (as the backend).
    const next = out.replace(SINGLE_QUOTED, '$1 $2');
    if (next === out) break;
    out = next;
  }
  return out.replace(STRAY_QUOTES, ' ').trim();
};

/** Why a shot plan is not renderable for clip length `L`: `timing` (shot count / timings / closing shot do not fit
 *  L; a regenerate fixes it) or `empty` (a required text field is blank, after the backend's quote stripping; the
 *  owner fills it in). Timing wins when both apply. Null when the plan looks valid. */
export type ShotPlanProblem = 'timing' | 'empty';

/** The FE mirror of what the backend validates (only what the UI needs): 2..8 shots, integer and contiguous from 0
 *  to exactly L, the last one the closing shot, then a non-empty action (quotes stripped) and camera on every
 *  shot and a non-empty style + music line. */
export const shotPlanProblem = (plan: ShotPlan | undefined, L: number | undefined): ShotPlanProblem | null => {
  if (!plan || !L || L <= 0) return 'timing';
  const { shots } = plan;
  if (!Array.isArray(shots) || shots.length < 2 || shots.length > 8) return 'timing';
  let at = 0;
  for (const s of shots) {
    if (s.start_s !== at || !(s.end_s > s.start_s)) return 'timing';
    at = s.end_s;
  }
  if (at !== L || shots[shots.length - 1].closing !== true) return 'timing';
  if (plan.style.trim() === '' || plan.music.trim() === '') return 'empty';
  return shots.some((s) => withoutQuotes(s.action) === '' || s.camera.trim() === '') ? 'empty' : null;
};

/** Whether a shot plan looks renderable for clip length `L` (see {@link shotPlanProblem}). */
export const shotPlanLooksValid = (plan: ShotPlan | undefined, L: number | undefined): boolean =>
  shotPlanProblem(plan, L) === null;

/** Canonical, round-trip-stable serialization of a shot plan for equality (fixed field order, trimmed text,
 *  normalized optionals), so a plan that came back through the backend compares equal to the live draft's. */
export const canonicalShotPlan = (plan: ShotPlan | undefined): string =>
  plan
    ? JSON.stringify([
        plan.style.trim(),
        (plan.world ?? '').trim(),
        plan.music.trim(),
        (plan.cast ?? []).map((c) => [c.id, c.description.trim(), c.source]),
        plan.shots.map((s) => [
          s.start_s,
          s.end_s,
          s.action.trim(),
          s.camera.trim(),
          (s.sound ?? '').trim(),
          s.closing === true,
        ]),
      ])
    : '';

/** The identity of one compile input (plan + model + length + aspect): the final-prompt panel recompiles when
 *  it changes, and a compose's own compiled prompt is shown as-is while it still matches. */
export const compileKeyOf = (
  plan: ShotPlan | undefined,
  videoModel: string,
  L: number | undefined,
  aspect: VideoAspectRatio,
): string => JSON.stringify([canonicalShotPlan(plan), videoModel, L ?? 0, aspect]);

/** Immutable: patch one shot of a plan by index (no-op for an out-of-range index). Timings are read-only. */
export const updateShot = (
  plan: ShotPlan,
  i: number,
  patch: Partial<Pick<ShotPlan['shots'][number], 'action' | 'camera' | 'sound'>>,
): ShotPlan => {
  if (i < 0 || i >= plan.shots.length) return plan;
  return { ...plan, shots: plan.shots.map((s, idx) => (idx === i ? { ...s, ...patch } : s)) };
};

/** Status copy for a single-clip run's `stage` (spec 4.6): `prepare` -> `generate` -> `finishing` -> `done`.
 *  Never parse a single-clip stage with {@link stageCopy} (it expects the v1 `frame-<i>` / `beat-<i>` ids). */
export const singleClipStageCopy = (stage: string | undefined, t: Translate): string => {
  switch (stage) {
    case 'prepare':
      return t('Preparing the opening frame');
    case 'generate':
      return t('Generating your clip');
    case 'finishing':
      return t('Adding logo & CTA');
    case 'done':
      return t('Finishing up');
    default:
      return t('Starting...');
  }
};

/** Cycling phrasings for a single-clip run's `activity` (spec 4.6 keys: `opening_frame`, `clip`, `clip_retry`,
 *  `overlay`, `upload`). Undefined for an absent / unknown key, so the caller falls back to the stage copy. */
export const singleClipActivityPhrases = (activity: string | undefined, t: Translate): string[] | undefined => {
  switch (activity) {
    case 'opening_frame':
      return [
        t('Preparing the opening frame'),
        t('Fitting your artwork to the video frame'),
        t("Matching your mood board's colors and light"),
      ];
    case 'clip':
      return [t('Generating your clip'), t('Directing every shot of your ad'), t('Adding music and sound effects')];
    case 'clip_retry':
      return [t('Trying the clip again with safer settings'), t("Adjusting your clip for the video service's rules")];
    case 'overlay':
      return [t('Adding logo & CTA'), t('Placing your logo and call to action on the final shot')];
    case 'upload':
      return [t('Saving your ad'), t('Finishing up')];
    default:
      return undefined;
  }
};

/**
 * Cycling phrasings for a video-ad run (either pipeline) that has not reached its first step yet. `queued`: no
 * worker has claimed it (the long lane is full, or the owner already has two long runs in flight). Any other
 * status (in practice `running` with no stage or activity yet): a worker claimed it and is setting the run up
 * before its first stage event. Durations are left to the hint under the status line.
 */
export const preStartPhrases = (status: string | undefined, t: Translate): string[] =>
  status === 'queued'
    ? [t('Queued for rendering'), t('Waiting for a free video slot'), t('Your ad keeps its place in line')]
    : [t('Starting...'), t('Setting up your video run'), t('Gathering your artwork and brand details')];

/**
 * Progress phase copy from a job's (completed, total). With total = 2N+1: the first N ticks are the
 * end frames ("Preparing frames"), the next N are the beat clips ("Animating beat k of n"), and the
 * last tick is assembly + upload ("Assembling your ad"). A not-yet-started total (0) reads as the
 * first phase.
 */
export const phaseCopy = (completed: number, total: number, t: Translate): string => {
  if (!Number.isFinite(total) || total <= 0) return t('Preparing frames');
  const n = (total - 1) / 2;
  if (completed < n) return t('Preparing frames');
  if (completed < 2 * n) return t('Animating beat {{k}} of {{n}}', { k: completed - n + 1, n });
  return t('Assembling your ad');
};

/**
 * Followable status copy for the node currently being worked, from the live job's `stage`. Preferred
 * over {@link phaseCopy} because the backend now streams the exact node id: `frame-<i>` (preparing beat
 * i's end-frame image), `beat-<i>` (animating beat i), `assembling` (stitching + branding), `done`
 * (finalising). Both `frame-<i>` and `beat-<i>` are 1-based. An unknown/absent stage reads as the
 * pre-start state.
 */
export const stageCopy = (stage: string | undefined, t: Translate): string => {
  if (stage === 'assembling') return t('Assembling your ad');
  if (stage === 'done') return t('Finishing up');
  const match = /^(frame|beat)-(\d+)$/.exec(stage ?? '');
  if (match) {
    const n = Number(match[2]);
    return match[1] === 'frame' ? t('Preparing scene {{n}}', { n }) : t('Animating beat {{n}}', { n });
  }
  return t('Starting...');
};

/**
 * Plain-language phrasings for the fine-grained step the backend reports in a live job's `activity`:
 * `<key>` or `<key>:<N>`, where N is the 1-based scene (beat). Two or three per step, first one the plainest,
 * so the run status line can cycle them while a long step (a scene animating for minutes) is in progress.
 * Preferred over {@link stageCopy} while a run is in flight, because `stage` sits on one node for minutes
 * while the backend works through several steps inside it. Returns undefined (so the caller falls back to
 * the stage copy) for an absent, malformed or unknown activity (a newer backend may add keys), and for a key
 * whose copy names a scene but arrives without a valid N. An N on a key whose copy does not name a scene
 * (e.g. `promo:2`) is ignored.
 */
export const activityPhrases = (activity: string | undefined, t: Translate): string[] | undefined => {
  const match = /^([a-z-]+)(?::(\d+))?$/.exec(activity ?? '');
  if (!match) return undefined;
  const key = match[1];
  // Scenes are 1-based, so `0` cannot name one and reads the same as a missing number.
  const num = match[2] === undefined ? 0 : Number(match[2]);
  const n = num >= 1 ? num : undefined;
  switch (key) {
    case 'fit':
      return [
        t('Fitting your artwork to the video frame'),
        t('Resizing your artwork for this format'),
        t('Making sure nothing important gets cropped'),
      ];
    case 'mood':
      return [
        t('Applying your mood board style'),
        t("Matching your mood board's colors and light"),
        t("Blending in your mood board's vibe"),
      ];
    case 'study':
      return [
        t('Taking a close look at your artwork'),
        t('Noting the colors, lighting and people'),
        t('Memorizing the look so every scene matches'),
      ];
    case 'promo':
      return [t('Getting your next promotion ready'), t('Switching to your next offer')];
    case 'assemble':
      return [
        t('Stitching your scenes together'),
        t('Balancing the sound and transitions'),
        t('Adding the finishing touches'),
      ];
    default:
      break;
  }
  // Every remaining known key names a scene, so it needs a valid N.
  if (n === undefined) return undefined;
  switch (key) {
    case 'frame':
      return [
        t('Designing how scene {{n}} ends', { n }),
        t('Sketching the final shot of scene {{n}}', { n }),
        t("Setting up scene {{n}}'s closing moment", { n }),
      ];
    case 'frame-fix':
      return [
        t('Tweaking scene {{n}} to follow content guidelines', { n }),
        t('Adjusting a few details in scene {{n}}', { n }),
      ];
    case 'animate':
      return [
        t('Bringing scene {{n}} to life', { n }),
        t('Animating scene {{n}}, frame by frame', { n }),
        t('Adding motion and sound to scene {{n}}', { n }),
      ];
    case 'reframe-person':
      return [
        t('Trying a new camera angle for scene {{n}}', { n }),
        t('Reframing the shot in scene {{n}}', { n }),
        t('Finding an angle the video service accepts'),
      ];
    case 'product-shot':
      return [
        t('Putting your offer front and center in scene {{n}}', { n }),
        t('Making your offer the star of scene {{n}}', { n }),
      ];
    case 'soften':
      return [
        t("Reworking scene {{n}} to meet the video service's rules", { n }),
        t('Adjusting scene {{n}} for the video service', { n }),
      ];
    case 'handoff':
      return [
        t('Lining up scene {{next}} to continue from scene {{n}}', { n, next: n + 1 }),
        t('Picking up where scene {{n}} left off', { n }),
      ];
    default:
      return undefined;
  }
};

/** True when a failed job's error came from the video provider's content policy (not a transient
 *  glitch), meaning a retry is pointless until the beat wording changes. The backend marks these with a
 *  `content policy:` prefix (see spark storyboardStageError); we also match the raw provider phrasing in
 *  case the message is surfaced unwrapped. Case-insensitive. */
export const isContentPolicyFailure = (error: string | undefined): boolean => {
  if (!error) return false;
  const e = error.toLowerCase();
  return (
    e.includes('content policy') ||
    e.includes('third-party content') ||
    e.includes('modify your prompt') ||
    e.includes('responsible ai') ||
    e.includes('safety filter')
  );
};

/** The human reason for a DETERMINISTIC provider rejection that is NOT a content-policy block (a bad or
 *  unsupported parameter, a prompt the provider refused for a non-policy reason, etc.), or undefined when
 *  the failure is not one. The backend marks these with a `provider rejected: <reason>` prefix (see spark
 *  storyboardStageError) so we can show the owner WHAT is wrong rather than a generic failure. Callers
 *  should test isContentPolicyFailure FIRST (a policy block gets its own actionable copy). */
export const providerRejectionReason = (error: string | undefined): string | undefined => {
  if (!error) return undefined;
  const m = /provider rejected:\s*(.+)/is.exec(error);
  const reason = m?.[1]?.trim();
  return reason ? reason : undefined;
};

/** A short, friendly timestamp for a finished-ad version label, e.g. "17 Sep, 14:52" (locale-aware). The
 *  version's `created_at` is epoch milliseconds; returns '' if it can't be formatted. */
export const formatVersionTimestamp = (ms: number): string => {
  if (!Number.isFinite(ms) || ms <= 0) return '';
  try {
    return new Date(ms).toLocaleString(undefined, {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
};

/** True when a "failed" job was actually STOPPED by the owner (the Stop button), not a real failure. The
 *  backend marks a cancelled run failed with a `cancelled_by_owner` marker, and a running job aborted mid-call
 *  surfaces the provider's "context canceled"; both contain "cancel". So the editor can show a neutral
 *  "Stopped" state instead of a scary failure. Case-insensitive. */
export const isCancelled = (error: string | undefined): boolean => {
  if (!error) return false;
  return /cancel/i.test(error);
};

/** True when a failed job's error is a per-call TIMEOUT (the video provider took longer than the client's
 *  deadline). Common with slow submit-and-poll models; the owner-facing hint is different from a generic
 *  failure ("took too long", try a shorter duration) so it gets its own branch. Case-insensitive. */
export const isTimeoutFailure = (error: string | undefined): boolean => {
  if (!error) return false;
  const e = error.toLowerCase();
  return e.includes('deadline exceeded') || e.includes('client.timeout') || e.includes('timeout exceeded');
};
